import { useFormContext } from '@calendly/form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingState from 'components/core/loading_state';
import { FullPageSurface } from 'features/app_shell';
import { useEventTypeEditorSidebar } from 'features/event_type_editor';
import { useEventTypeContext } from 'features/event_type_provider';
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path';
import { ET_EDITOR_VARIANTS } from 'shared/constants/et_editor';
import { EditorHeader } from './components/editor_header';
import { EditorPreviewProvider } from './components/editor_preview_context';
import { EditorPrimaryActions } from './components/editor_primary_actions';
import { PreviewModal } from './components/preview_modal';
import { useEtEditorQueryParam } from './editor_variant/etEditorQueryParam';
import { RootForm } from './form/root_form';
import { PRIMARY_ACTIONS_PLACEMENT } from './hooks/use_editor_primary_actions';
import { LargeLayout } from './layouts/large_layout';
import { MediumLayout } from './layouts/medium_layout';
import { SmallLayout } from './layouts/small_layout';
import { eventTypeEditorCrudTabs } from './tabs/registry';
import { EDITOR_TIER, useEditorLayout } from './use_editor_layout';
const meetingTypesScopeFromEventType = eventType => {
  const ownerType = eventType?.owner_type ?? eventType?.ownerType;
  const ownerId = eventType?.owner_id ?? eventType?.ownerId;

  // Mirror classic editor redirects: Organization-owned (shared) ETs use the
  // personal meeting-types list (`user/me`), not `user/<organizationId>`.
  if (ownerType === 'Page') return `team/${ownerId}`;
  if (ownerType === 'Organization') return 'user/me';
  return `user/${ownerId ?? 'me'}`;
};
const shellCss = "s1v1em3i";
const LAYOUT_BY_TIER = {
  [EDITOR_TIER.large]: LargeLayout,
  [EDITOR_TIER.medium]: MediumLayout,
  [EDITOR_TIER.small]: SmallLayout
};

/** Syncs Formik dirty state to a ref accessible outside the form tree. */
const DirtySyncer = ({
  dirtyRef
}) => {
  const {
    dirty
  } = useFormContext();
  dirtyRef.current = dirty;
  return null;
};
const EditorContent = ({
  id,
  tabKey,
  eventType,
  meetingTypesPath
}) => {
  const history = useHistory();
  const {
    search
  } = useLocation();
  const {
    tier,
    mode,
    activeTab,
    showBackToMenu
  } = useEditorLayout({
    tabKey
  });
  const dirtyRef = useRef(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const onSelectTab = nextTabKey => {
    history.push(`/event_types/${id}/editor/${nextTabKey}${search}`);
  };
  const onBackToMenu = () => {
    history.push(`/event_types/${id}/editor${search}`);
  };
  const onBeforeClose = useCallback(location => {
    // Allow any push that stays within this event type's editor (tab changes, back-to-menu).
    if (location?.pathname?.startsWith(`/event_types/${id}/editor`)) return true;
    return !dirtyRef.current;
  }, [id]);
  const Layout = LAYOUT_BY_TIER[tier];
  return <RootForm activeTab={activeTab} className={shellCss} data-testid="event-type-editor-crud-shell">
      {({
      etLocks,
      invalidTabKeys,
      lockingDisables,
      panelKeys,
      panelPropsByKey
    }) => <FullPageSurface returnTo={meetingTypesPath} onBack={showBackToMenu ? onBackToMenu : undefined} onBeforeClose={onBeforeClose} hasHeaderBorder={tier !== EDITOR_TIER.small} header={<EditorHeader eventType={eventType} showPreviewButton={tier === EDITOR_TIER.medium} onOpenPreview={() => setIsPreviewOpen(true)} showPrimaryActions={tier !== EDITOR_TIER.small} />} footer={tier === EDITOR_TIER.small ? <EditorPrimaryActions eventType={eventType} placement={PRIMARY_ACTIONS_PLACEMENT.footer} /> : undefined}>
          <EditorPreviewProvider activeTabKey={activeTab.key}>
            <DirtySyncer dirtyRef={dirtyRef} />
            <Layout eventType={eventType} activeTab={activeTab} tabs={eventTypeEditorCrudTabs} mode={mode} showBackToMenu={showBackToMenu} onSelectTab={onSelectTab} onBackToMenu={onBackToMenu} etLocks={etLocks} invalidTabKeys={invalidTabKeys} lockingDisables={lockingDisables} panelKeys={panelKeys} panelPropsByKey={panelPropsByKey} />
          </EditorPreviewProvider>
          {tier === EDITOR_TIER.medium && <PreviewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />}
        </FullPageSurface>}
    </RootForm>;
};
export const EventTypeEditorCrudEditor = ({
  match
}) => {
  const {
    id,
    tab
  } = match.params;
  const history = useHistory();
  const editorVariant = useEtEditorQueryParam();
  const {
    openSidebar: openEventTypeSidebar
  } = useEventTypeEditorSidebar();
  const {
    eventType,
    isLoading: isEventTypeLoading,
    error
  } = useEventTypeContext();
  const meetingTypesPath = useMeetingTypesPath(eventType ? meetingTypesScopeFromEventType(eventType) : 'user/me');
  const shouldExitToClassic = editorVariant === ET_EDITOR_VARIANTS.CLASSIC;

  // `?et_editor=classic` → Meeting Types + classic rail. Do not keep the param
  // on the list URL or later Edits stay forced to classic under the flag.
  useEffect(() => {
    if (isEventTypeLoading || !shouldExitToClassic) return;
    if (eventType) {
      openEventTypeSidebar({
        id: eventType.id,
        type: eventType.type,
        ownerType: eventType.owner_type,
        ownerId: eventType.owner_id,
        kind: eventType.kind,
        poolingType: eventType.pooling_type
      });
    }
    history.replace(meetingTypesPath);
  }, [eventType, history, isEventTypeLoading, meetingTypesPath, openEventTypeSidebar, shouldExitToClassic]);
  if (isEventTypeLoading) return <LoadingState />;
  if (shouldExitToClassic) return <LoadingState />;
  if (error || !eventType) return null;
  return <EditorContent id={id} tabKey={tab} eventType={eventType} meetingTypesPath={meetingTypesPath} />;
};

require("./editor.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./editor.js");